import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Lightbulb, Pencil, Code2, Rocket } from "lucide-react";

const processSteps = [
  {
    icon: Lightbulb,
    title: "Discovery",
    description:
      "We start by understanding your business, goals, and challenges. Every solution begins with listening.",
    color: "from-[oklch(0.65_0.15_230)] to-[oklch(0.55_0.18_240)]",
  },
  {
    icon: Pencil,
    title: "Design",
    description:
      "Our team crafts beautiful, intuitive designs that align with your brand and delight your users.",
    color: "from-[oklch(0.58_0.22_345)] to-[oklch(0.68_0.20_335)]",
  },
  {
    icon: Code2,
    title: "Development",
    description:
      "We build your solution using cutting-edge technology, ensuring performance, security, and scalability.",
    color: "from-[oklch(0.65_0.15_230)] to-[oklch(0.58_0.22_345)]",
  },
  {
    icon: Rocket,
    title: "Launch & Support",
    description:
      "We deploy your solution and provide ongoing support to ensure continued success and growth.",
    color: "from-[oklch(0.58_0.22_345)] to-[oklch(0.48_0.24_355)]",
  },
];

export default function ProcessSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="process" className="py-24 md:py-32 bg-background" ref={ref}>
      <div className="container">
        {/* Section Header */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
        >
          <motion.span
            className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary font-semibold text-sm mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.6 }}
          >
            Our Process
          </motion.span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            From Concept to{" "}
            <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Reality
            </span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            A proven process that transforms your vision into powerful digital solutions.
          </p>
        </motion.div>

        {/* Process Timeline */}
        <div className="relative max-w-5xl mx-auto">
          {/* Connecting Line */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-secondary to-primary opacity-20" />

          {/* Process Steps */}
          <div className="space-y-16 md:space-y-24">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.title}
                className={`relative flex flex-col md:flex-row items-center gap-8 ${
                  index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                }`}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={
                  isInView
                    ? { opacity: 1, x: 0 }
                    : { opacity: 0, x: index % 2 === 0 ? -50 : 50 }
                }
                transition={{ duration: 0.8, delay: index * 0.2 }}
              >
                {/* Content */}
                <div className={`flex-1 ${index % 2 === 0 ? "md:text-right" : "md:text-left"}`}>
                  <motion.div
                    className="inline-block"
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  >
                    <h3 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
                      {step.title}
                    </h3>
                    <p className="text-lg text-muted-foreground leading-relaxed max-w-md mx-auto md:mx-0">
                      {step.description}
                    </p>
                  </motion.div>
                </div>

                {/* Icon Circle */}
                <motion.div
                  className="relative z-10 flex-shrink-0"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <div
                    className={`w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br ${step.color} p-5 md:p-6 shadow-lg`}
                  >
                    <step.icon className="w-full h-full text-white" />
                  </div>
                  {/* Pulse effect */}
                  <motion.div
                    className={`absolute inset-0 rounded-full bg-gradient-to-br ${step.color} opacity-30`}
                    animate={{
                      scale: [1, 1.3, 1],
                      opacity: [0.3, 0, 0.3],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: index * 0.5,
                    }}
                  />
                </motion.div>

                {/* Step Number */}
                <div className={`flex-1 ${index % 2 === 0 ? "md:text-left" : "md:text-right"}`}>
                  <motion.div
                    className="inline-block text-8xl md:text-9xl font-bold opacity-5"
                    initial={{ opacity: 0 }}
                    animate={isInView ? { opacity: 0.05 } : { opacity: 0 }}
                    transition={{ duration: 0.8, delay: index * 0.2 }}
                  >
                    0{index + 1}
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
